<template>
  <div class="main-content">
    <div class="section__content section__content--p30">
      <div class="container-fluid file-job-dashboard">
        <div class="row">
          <div class="col-xl-12">
            <div class="chart-info">
              <div class="chart-info__left">
                <h3 class="title-1"><i class="zmdi zmdi-calendar-note"></i>
                  {{ text('overview') }}
                </h3>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6 col-lg-3">
            <div class="statistic__item">
              <h2 class="number">{{ totalJobs }}</h2>
              <span class="desc">
                {{ text('total_jobs') }}
              </span>
              <div class="icon">
                <i class="zmdi zmdi-collection-video"></i>
              </div>
            </div>
          </div>
          <div class="col-md-6 col-lg-3">
            <div class="statistic__item">
              <h2 class="number">{{ transcodingMinutes }}</h2>
              <span class="desc">{{ text('encoding_minutes') }}</span>
              <div class="icon">
                <i class="zmdi zmdi-timer"></i>
              </div>
            </div>
          </div>
          <div class="col-md-6 col-lg-3">
            <div class="statistic__item">
              <h2 class="number">{{ profiles.length }}</h2>
              <span class="desc">{{ text('profilesc_count') }}</span>
              <div class="icon">
                <i class="fas fa-sliders-h"></i>
              </div>
            </div>
          </div>
          <div class="col-md-6 col-lg-3">
            <div class="statistic__item">
              <h2 class="number">{{ watchFolders.length }}</h2>
              <span class="desc">{{ text('watch_folders_count') }}</span>
              <div class="icon">
                <i class="zmdi zmdi-folder-outline"></i>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-lg-6">
            <div class="au-card">
              <div class="au-card-inner">
                <h3 class="title-2 tm-b-5">{{ text('last_encodings') }}</h3>
                <div class="row no-gutters au-task-list">
                  <table class="m-t-15 align-middle mb-0 table table-borderless table-hover table-striped">
                    <tbody>
                    <tr v-for="(job, i) of jobs"
                        :key="job.id"
                        v-if="jobs.length < 9">
                      <td class="text-muted text-left" @click="showJobDetail(job.id)">
                        #{{ totalJobs - i }}
                      </td>
                      <td @click="showJobDetail(job.id)" style="max-width: 250px; word-break: break-all;">
                        {{ job.name }}
                      </td>
                      <td @click="showJobDetail(job.id)" style="width: 150px;">
                        <div v-if="jobStatus(job) !== 'in_progress'">
                          <i class="fa fa-clock job-status--paused"
                             v-if="jobStatus(job) === 'pending'">
                          </i>
                          <i class="fa fa-circle-notch fa-spin job-status--finished"
                             v-if="jobStatus(job) === 'processing'">
                          </i>
                          <i class="fa fa-check-circle job-status--finished"
                             v-if="jobStatus(job) === 'done'">
                          </i>
                          <i class="fa fa-exclamation-circle job-status--failed"
                             v-if="jobStatus(job) === 'input_failed' || jobStatus(job) === 'output_failed'">
                          </i>
                          <span class="p-l-10">{{ text(jobStatus(job)) }}</span>
                        </div>
                        <div v-else>
                          <div class="text-center">{{ job.outputs[0].progress }}%</div>
                          <div class="progress-bar-xs progress progress-bar-animated-alt">
                            <div class="progress-bar job-status--bg-process"
                                 role="progressbar"
                                 :style="{ width: job.outputs[0].progress + '%' }"></div>
                          </div>
                        </div>
                    </td>
                  </tr>
                  <tr v-if="!loading && !jobs.length" class="no-hover">
                    <td class="text-center" colspan="7">
                      <div class="m-b-15 m-t-15">
                        {{ text('no_jobs') }}
                      </div>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-6">
          <div class="au-card chart-percent-card">
            <div class="au-card-inner">
              <h3 class="title-2 tm-b-5">{{ text('job_status_report') }}</h3>
              <div class="row no-gutters au-task-list">
                <div class="col-xl-6">
                  <div class="chart-note-wrap">
                    <div class="chart-note mr-0 d-block">
                      <span class="dot dot--blue"></span>
                      <span>{{ text('processing') }} ({{ totalProcessing }})</span>
                    </div>
                    <div class="chart-note mr-0 d-block">
                      <span class="dot dot--green"></span>
                      <span>{{ text('success') }} ({{ totalJobs - totalFailed }})</span>
                    </div>
                    <div class="chart-note mr-0 d-block">
                      <span class="dot dot--red"></span>
                      <span>{{ text('failed') }} ({{ totalFailed }})</span>
                    </div>
                  </div>
                </div>
                <div class="col-xl-6 m-t-15" v-if="totalJobs">
                  <report-chart :chartdata="chartData"></report-chart>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  </div>
</template>

<script>
import ReportChart from './ReportChart.vue';
import { INPUT_STATES, OUTPUT_STATES } from '@/components/transcoding/shared/types';

export default {
  components: {
    ReportChart,
  },
  created() {
    this.$store.commit('setPageLoader');
    this.loadData();
    this.pollData();
  },
  beforeDestroy() {
    clearInterval(this.polling);
  },
  data() {
    return {
      inputStates: INPUT_STATES,
      outputState: OUTPUT_STATES,
      polling: null,
    };
  },
  computed: {
    chartData() {
      return {
        labels: ['Processing', 'Success', 'Failed'],
        datasets: [{
          data: [this.totalProcessing, this.totalJobs - this.totalFailed, this.totalFailed],
          fill: true,
          backgroundColor: [
            'rgb(0, 181, 233)',
            'rgb(11, 176, 102)',
            'rgb(250, 66, 81)',
          ],
          borderWidth: 2,
        }],
      };
    },
    loading() {
      return this.$store.getters.isDataLoaded;
    },
    totalEncodings() {
      return this.$store.getters['jobsOverview/getTotalEncodings'];
    },
    transcodingMinutes() {
      return this.$store.getters['jobsUsage/getFileTranscodingMinutes'];
    },
    totalJobs() {
      return this.$store.getters['jobsOverview/getTotalFileJobs'];
    },
    totalFailed() {
      return this.$store.getters['jobsOverview/getFailedJobs'];
    },
    totalProcessing() {
      return this.$store.getters['jobsOverview/getProcessingJobs'];
    },
    profiles() {
      return this.$store.getters['encodingProfiles/getProfiles'];
    },
    watchFolders() {
      return this.$store.getters['watchFolders/getWatchFolders'];
    },
    jobs() {
      return this.$store.getters['jobs/getFileJobs'];
    },
    dict() {
      return this.$store.getters.dictionary;
    },
  },
  methods: {
    pollData() {
      this.polling = setInterval(() => {
        this.loadData();
      }, 10000);
    },
    loadData() {
      this.$store.dispatch('jobsOverview/getOverview')
        .then(() => {
          const args = {
            limit: 8,
            offset: 0,
          };
          this.$store.dispatch('jobs/getFileJobs', args)
            .catch((errorMsg) => {
              this.$toasted.error(errorMsg, {
                theme: 'toasted-primary',
                position: 'top-center',
                duration: 10000,
              });
            })
            .finally(() => this.$store.commit('resolvePageLoader'));
          this.$store.dispatch('jobsUsage/getUsage');
          this.$store.dispatch('encodingProfiles/getProfiles');
          this.$store.dispatch('watchFolders/getWatchFolders');
        })
        .catch((errorMsg) => {
          this.$store.commit('resolvePageLoader')
          this.$toasted.error(errorMsg, {
            theme: 'toasted-primary',
            position: 'top-center',
            duration: 10000,
          });
        });
    },
    jobStatus(job) {
      if (job.input.state === 'pending') {
        return 'pending';
      }
      if (job.input.state === 'processing') {
        return 'processing';
      }
      if (job.input.state === 'error') {
        return 'input_failed';
      }
      if (job.outputs[0].state === 'pending') {
        return 'processing';
      }
      if (job.outputs[0].state === 'error') {
        return 'output_failed';
      }
      if (job.outputs[0].state === 'success') {
        return 'done';
      }
      return 'in_progress';
    },
    showJobDetail(id) {
      this.$router.push({
        name: 'FileTranscodingJobDetailPage',
        params: {
          id,
        },
      });
    },
    text(code) {
      // eslint-disable-next-line no-prototype-builtins
      if (this.dict.hasOwnProperty(code)) {
        return this.dict[code];
      }
      return code;
    },
  },
};
</script>
